import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Autocomplete, Checkbox, CircularProgress, Stack, Typography, useTheme } from '@mui/material'
import { Order, HeadCell } from '../../../utils/tableFunctions'
import TableButtonDelete from '../../table/TableButtonDelete'
import EnhancedTableHead from '../../table/EnhancedTableHead'
import { ProductOfEventDto } from '../../../services/api/sharedTypes/Product'
import CustomTextField from '../../common/CustomTextField'
import { EventDto } from '../../../services/api/sharedTypes/Event'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import CustomTableCell from '../../common/CustomTableCell'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import { calculateTimeDifferenceInTimeSpan } from '../../../utils/global-utils'
import TimeSpanTextField from '../../common/TimeSpanTextField'
import {
    convertProductQuantityErrorTypeToString,
    ProductQuantityErrorType,
    convertProductOfEventDtoToProductWithStatisticsDto,
} from '../../../utils/product-utils'
import { IdNameDto } from '../../../services/api/sharedTypes/Common'
import SmallPlusButton from '../../common/SmallPlusButton'

const headCells: readonly HeadCell[] = [
    {
        id: 'productName',
        numeric: false,
        disablePadding: false,
        label: 'Služba',
    },
    {
        id: 'operatingTime',
        numeric: false,
        disablePadding: true,
        label: 'Provoz (hh:mm)',
        centered: true,
    },
    {
        id: 'revenue',
        numeric: true,
        disablePadding: true,
        label: 'Výnos',
    },
    {
        id: 'costs',
        numeric: true,
        disablePadding: true,
        label: 'Náklady',
    },
    {
        id: 'quantity',
        numeric: true,
        disablePadding: true,
        label: 'Počet',
    },
    {
        id: 'isConfirmed',
        numeric: true,
        disablePadding: false,
        label: 'Služba potvrzena',
        component: <CheckCircleOutlineRoundedIcon />,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface ProductTableProps {
    event: EventDto
    lookUpProducts: IdNameDto[]
    areProductQuantitiesLoading: boolean
    setEvent: (value: React.SetStateAction<EventDto | undefined>) => void
    getUsedProducts: () => { productName: string; errorType: ProductQuantityErrorType }[]
    setIsProductChanged?: (value: React.SetStateAction<boolean>) => void
    recalculateProductQuantities: () => void
}

const ProductTable: React.FC<ProductTableProps> = ({
    setEvent,
    event,
    areProductQuantitiesLoading,
    lookUpProducts,
    getUsedProducts,
    setIsProductChanged,
    recalculateProductQuantities,
}: ProductTableProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<keyof ProductOfEventDto>()
    const [lastQuantity, setLastQuantity] = React.useState<number>(-1)
    const [isProductIdChanged, setIsProductIdChanged] = React.useState<boolean>(true)

    const productsForTable = event.products.sort((a: ProductOfEventDto, b: ProductOfEventDto) => {
        if (orderBy) {
            if ((a[orderBy] ?? 0) < (b[orderBy] ?? 0)) {
                return order === 'asc' ? -1 : 1
            }
            if ((a[orderBy] ?? 0) > (b[orderBy] ?? 0)) {
                return order === 'asc' ? 1 : -1
            }
        }
        return 0
    })

    const usedProducts = React.useMemo(() => getUsedProducts(), [lastQuantity, getUsedProducts])

    React.useEffect(() => {
        if (isProductIdChanged) {
            setIsProductIdChanged(false)
            recalculateProductQuantities()
            getUsedProducts()
        }
    }, [isProductIdChanged])

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property as keyof ProductOfEventDto)
    }

    const addEmptyProduct = () => {
        setEvent({
            ...event,
            products: [
                ...event.products,
                {
                    productId: '',
                    costs: 0,
                    quantity: 1,
                    revenue: 0,
                    isConfirmed: false,
                    operatingTime: calculateTimeDifferenceInTimeSpan(event.startsAt, event.endsAt),
                    productName: '',
                },
            ],
        })
    }

    return (
        <>
            <Stack direction='row' justifyContent='space-between' mb={2}>
                <Typography variant='h6' color='primary'>
                    Atrakce a služby
                </Typography>
                <SmallPlusButton onClick={() => {
                    const isThereEmptyProduct = event.products.find((product) => product.productId === '')
                    if (isThereEmptyProduct)
                        return dispatch(displayMessage({ message: 'Nejdříve zvolte prázdnou službu', type: 'warning' }))

                    addEmptyProduct()
                }} />
            </Stack>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy ?? ''}
                            onRequestSort={handleRequestSort}
                            rowCount={productsForTable.length}
                            headCells={headCells}
                        />
                        <TableBody>
                            {productsForTable.map((row, index: number) => {
                                return (
                                    <TableRow
                                        tabIndex={-1}
                                        key={index}
                                        style={{
                                            height: '42px',
                                            backgroundColor: row.isConfirmed ? theme.palette.tableRow?.background.paid : '',
                                        }}
                                    >
                                        <CustomTableCell>
                                            <Autocomplete
                                                options={lookUpProducts
                                                    .filter(
                                                        (product) =>
                                                            !event.products.some(
                                                                (localProduct) => localProduct.productId === product.id
                                                            )
                                                    )
                                                    .sort((a, b) => {
                                                        if (a.name > b.name) return 1
                                                        if (a.name < b.name) return -1
                                                        return 0
                                                    })}
                                                getOptionLabel={(option) => option.name}
                                                value={
                                                    lookUpProducts.find((product) => product.id === row.productId) ??
                                                    convertProductOfEventDtoToProductWithStatisticsDto(productsForTable[index]) ??
                                                    null
                                                }
                                                size='small'
                                                onChange={(e, newValue) => {
                                                    const isProductInList = productsForTable.some(
                                                        (localProduct) => localProduct.productId === newValue?.id
                                                    )
                                                    if (isProductInList)
                                                        return dispatch(
                                                            displayMessage({
                                                                message: 'Služba je již v přiřazena k této události',
                                                                type: 'warning',
                                                            })
                                                        )

                                                    if (newValue) {
                                                        setEvent({
                                                            ...event,
                                                            products: productsForTable.map((product) => {
                                                                if (product.productId === row.productId) {
                                                                    return {
                                                                        ...product,
                                                                        productId: newValue.id ?? '',
                                                                        productName: newValue.name,
                                                                    }
                                                                }
                                                                return product
                                                            }),
                                                        })
                                                        setIsProductChanged && setIsProductChanged(true)
                                                        setIsProductIdChanged(true)
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <CustomTextField
                                                        {...params}
                                                        size='small'
                                                        sx={{ minWidth: '170px' }}
                                                        isSmall
                                                        textSize={14}
                                                        isPaddingDisabled
                                                    />
                                                )}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell width={105} disablePadding>
                                            <TimeSpanTextField
                                                isSmall
                                                width='42px'
                                                minWidth='100px'
                                                value={row.operatingTime}
                                                onChange={(value) => {
                                                    setEvent({
                                                        ...event,
                                                        products: productsForTable.map((product) => {
                                                            if (product.productId === row.productId) {
                                                                return {
                                                                    ...product,
                                                                    operatingTime: value,
                                                                }
                                                            }
                                                            return product
                                                        }),
                                                    })
                                                    setIsProductChanged && setIsProductChanged(true)
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell width={78} disablePadding>
                                            <CustomTextField
                                                value={row.revenue || ''}
                                                size='small'
                                                style={{ width: '69px' }}
                                                isSmall
                                                onChange={(e) => {
                                                    if (isNaN(Number(e.target.value))) return
                                                    setIsProductChanged && setIsProductChanged(true)
                                                    setEvent({
                                                        ...event,
                                                        products: productsForTable.map((product) => {
                                                            if (product.productId === row.productId) {
                                                                return {
                                                                    ...product,
                                                                    revenue: Number(e.target.value),
                                                                }
                                                            }
                                                            return product
                                                        }),
                                                    })
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell width={50} disablePadding>
                                            <CustomTextField
                                                value={row.costs || ''}
                                                size='small'
                                                isSmall
                                                style={{ width: '69px' }}
                                                onChange={(e) => {
                                                    if (isNaN(Number(e.target.value))) return
                                                    setIsProductChanged && setIsProductChanged(true)
                                                    setEvent({
                                                        ...event,
                                                        products: productsForTable.map((product) => {
                                                            if (product.productId === row.productId) {
                                                                return {
                                                                    ...product,
                                                                    costs: Number(e.target.value),
                                                                }
                                                            }
                                                            return product
                                                        }),
                                                    })
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell width={50} disablePadding>
                                            <CustomTextField
                                                value={row.quantity || ''}
                                                size='small'
                                                isSmall
                                                style={{ width: '45px' }}
                                                onChange={(e) => {
                                                    if (isNaN(Number(e.target.value))) return
                                                    setIsProductChanged && setIsProductChanged(true)
                                                    setEvent({
                                                        ...event,
                                                        products: productsForTable.map((product) => {
                                                            if (product.productId === row.productId) {
                                                                return {
                                                                    ...product,
                                                                    quantity: Number(e.target.value),
                                                                }
                                                            }
                                                            return product
                                                        }),
                                                    })
                                                    setLastQuantity(Number(e.target.value))
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell width={40}>
                                            <Checkbox
                                                checked={row.isConfirmed ? true : false}
                                                onChange={(e) => {
                                                    setEvent({
                                                        ...event,
                                                        products: productsForTable.map((product) => {
                                                            if (product.productId === row.productId) {
                                                                return {
                                                                    ...product,
                                                                    isConfirmed: e.target.checked,
                                                                }
                                                            }
                                                            return product
                                                        }),
                                                    })
                                                    setIsProductChanged && setIsProductChanged(true)
                                                }}
                                            />
                                        </CustomTableCell>
                                        <CustomTableCell align='right' width={48}>
                                            <Stack direction='row' justifyContent='end'>
                                                <TableButtonDelete
                                                    nameOfEntityInDialogMessage={row.productName}
                                                    onClick={() => {
                                                        setEvent({
                                                            ...event,
                                                            products: productsForTable.filter(
                                                                (p) => p.productId !== row.productId
                                                            ),
                                                        })
                                                        setIsProductIdChanged(true)
                                                    }}
                                                />
                                            </Stack>
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {areProductQuantitiesLoading ? (
                    <CircularProgress />
                ) : usedProducts.length > 0 ? (
                    <Stack>
                        {usedProducts.some((p) => p.errorType === ProductQuantityErrorType.Overused) && (
                            <Typography variant='body2' color={theme.palette.warning.dark} style={{ height: theme.spacing(4) }}>
                                <Stack direction='row' spacing={theme.spacing(1)} alignItems='center'>
                                    <WarningAmberOutlinedIcon />
                                    {usedProducts
                                        .filter((p) => p.errorType === ProductQuantityErrorType.Overused)
                                        .map((p) => p.productName)
                                        .join(', ')}
                                    {convertProductQuantityErrorTypeToString(ProductQuantityErrorType.Overused)}
                                </Stack>
                            </Typography>
                        )}
                        {usedProducts.some((p) => p.errorType === ProductQuantityErrorType.UsedElsewhere) && (
                            <Typography variant='body2' color={theme.palette.warning.dark} style={{ height: theme.spacing(4) }}>
                                <Stack direction='row' spacing={theme.spacing(1)} alignItems='center'>
                                    <WarningAmberOutlinedIcon />
                                    {usedProducts
                                        .filter((p) => p.errorType === ProductQuantityErrorType.UsedElsewhere)
                                        .map((p) => p.productName)
                                        .join(', ')}
                                    {convertProductQuantityErrorTypeToString(ProductQuantityErrorType.UsedElsewhere)}
                                </Stack>
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <div></div>
                )}
            </Box>
        </>
    )
}

export default ProductTable
